import React from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { ArrowRight, Lock } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import ThymosinAlpha1Content from './peptides/ThymosinAlpha1';
import LL37Content from './peptides/LL37';

// Define peptide categories
const peptideCategories: {
  id: string;
  name: string;
  peptides: {
    id: string;
    name: string;
    fullName?: string;
    description?: string;
    content?: React.ReactNode;
  }[];
}[] = [
  {
    id: 'immunomodulatory',
    name: 'Immunomodulatory Peptides',
    peptides: [
      {
        id: 'thymosin-alpha-1',
        name: 'Thymosin Alpha-1',
        fullName: 'Thymosin Alpha-1 (TA-1, Thymalfasin)',
        description: 'A thymic peptide that modulates immunity and inflammation, enhancing T-cell and dendritic cell function.',
        content: <ThymosinAlpha1Content />
      },
      {
        id: 'll-37',
        name: 'LL-37',
        fullName: 'LL-37 (Cathelicidin Antimicrobial Peptide)',
        description: 'A human host-defense peptide with antimicrobial, immunomodulatory, and wound healing properties.',
        content: <LL37Content />
      }
    ]
  },
  {
    id: 'metabolic',
    name: 'Metabolic Peptides',
    peptides: [
      {
        id: 'ipamorelin',
        name: 'Ipamorelin',
        fullName: 'Ipamorelin (Growth Hormone Secretagogue)',
        description: 'A synthetic growth hormone secretagogue that mimics ghrelin, stimulating pulsatile growth hormone release without significantly affecting cortisol or prolactin levels.',
        // Note: We'll need to create a standalone component for this in the non-modular version
        content: <div className="p-8 text-center">
          <p className="text-gray-500 text-lg">The Ipamorelin content is available in the modular version.</p>
          <p className="text-gray-400 mt-2">Please visit <a href="/peptide-protocols-modular#metabolic/ipamorelin" className="text-blue-500 hover:underline">modular version</a> to view full content.</p>
        </div>
      }
    ]
  }
];

// Define sidebar links based on peptide categories
const sidebarLinks = peptideCategories.map(category => ({
  title: category.name,
  id: category.id,
  peptides: category.peptides.map(peptide => ({
    title: peptide.name,
    id: peptide.id,
  }))
}));

export default function PeptideProtocols() {
  const [activeCategory, setActiveCategory] = React.useState('');
  const [activePeptide, setActivePeptide] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  
  // Check if user is premium
  const { data: authData, isLoading: authLoading } = useQuery<{
    isAuthenticated: boolean;
    user?: {
      id: number;
      username: string;
      email: string;
      subscriptionStatus: string;
    };
  }>({
    queryKey: ['/api/auth/status'],
    retry: false,
  });
  
  // Check for all valid premium statuses including 'mentorship'
  const isPremium = !authLoading && 
    authData?.isAuthenticated && 
    (authData?.user?.subscriptionStatus?.toLowerCase() === 'active' || 
     authData?.user?.subscriptionStatus?.toLowerCase() === 'trialing' ||
     authData?.user?.subscriptionStatus?.toLowerCase() === 'mentorship');

  // Effect to handle URL hash changes
  React.useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash && peptideCategories.length > 0) {
      const [categoryId, peptideId] = hash.split('/');
      if (categoryId && peptideCategories.some(cat => cat.id === categoryId)) {
        setActiveCategory(categoryId);
        
        if (peptideId) {
          const category = peptideCategories.find(cat => cat.id === categoryId);
          if (category && category.peptides.some(pep => pep.id === peptideId)) {
            setActivePeptide(peptideId);
          }
        }
      }
    } else if (peptideCategories.length > 0) {
      // Set defaults if no hash
      setActiveCategory(peptideCategories[0].id);
      if (peptideCategories[0].peptides.length > 0) {
        setActivePeptide(peptideCategories[0].peptides[0].id);
      }
    }
  }, [location]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Get active content
  const getActiveContent = () => {
    if (peptideCategories.length === 0) return null;
    
    const category = peptideCategories.find(cat => cat.id === activeCategory);
    if (!category) return null;
    
    const peptide = category.peptides.find(pep => pep.id === activePeptide);
    return peptide?.content || null;
  };

  // Show premium content restriction if not premium
  if (!isPremium) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container mx-auto px-4 py-8 flex-grow">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-2">Premium Peptide Protocols</h1>
          <p className="text-xl text-[#7F8C8D] mb-6">
            Comprehensive clinical guides for using peptides in longevity and regenerative medicine practice
          </p>
          
          <div className="bg-gradient-to-r from-[#F8F9FA] to-white border border-gray-200 rounded-xl p-8 shadow-sm max-w-3xl mx-auto text-center">
            <div className="inline-block p-3 bg-yellow-100 rounded-full mb-4">
              <Lock className="h-8 w-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2C3E50] mb-2">Premium Content</h2>
            <p className="text-[#7F8C8D] mb-6">
              This section contains premium peptide protocols and clinical guides available exclusively for members.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/membership">
                <Button size="lg" className="bg-[#3498DB] hover:bg-[#2980B9]">
                  Upgrade to Premium
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link href="/login">
                <Button variant="outline" size="lg">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow">
        <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-2">Premium Peptide Protocols</h1>
        <p className="text-xl text-[#7F8C8D] mb-8">
          Comprehensive clinical guides for using peptides in longevity and regenerative medicine practice
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation sidebar */}
          <div className="col-span-1">
            <div className="sticky top-20">
              <div className="mb-6">
                <Input
                  type="search"
                  placeholder="Search protocols..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-4">
                {peptideCategories.length === 0 ? (
                  <p className="text-gray-500 italic">No peptide categories available yet</p>
                ) : (
                  peptideCategories.map((category) => (
                    <div key={category.id} className="space-y-2">
                      <h3 className="font-medium text-[#2C3E50]">{category.name}</h3>
                      <div className="ml-2 space-y-1">
                        {category.peptides.map((peptide) => (
                          <a
                            key={peptide.id}
                            href={`#${category.id}/${peptide.id}`}
                            className={`block py-1 px-2 rounded text-sm ${
                              activeCategory === category.id && activePeptide === peptide.id
                                ? 'bg-[#EBF5FB] text-[#3498DB] font-medium'
                                : 'text-[#7F8C8D] hover:bg-gray-100'
                            }`}
                          >
                            {peptide.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          
          {/* Main content area */}
          <div className="col-span-1 lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {peptideCategories.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500 text-lg">Peptide protocols are coming soon!</p>
                  <p className="text-gray-400 mt-2">We're currently preparing comprehensive protocols for you.</p>
                </div>
              ) : getActiveContent()}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}